import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Popconfirm, Space, Table, Tabs, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GradeCatalogTab } from "@/modules/department-grades/components/GradeCatalogTab";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { usePermission } from "@/shared/hooks/usePermission";
import { PositionFormModal } from "../components/PositionFormModal";
import {
	useCreatePosition,
	useDeletePosition,
	usePositionsList,
	useUpdatePosition,
} from "../hooks/usePositions";
import type { Position, PositionFormValues } from "../types";

const { Title } = Typography;

export function PositionsPage() {
	const { t } = useTranslation("positions");
	const { can } = usePermission();
	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState<Position | null>(null);

	const { data: positions = [], isLoading, isError } = usePositionsList();

	const createPosition = useCreatePosition();
	const updatePosition = useUpdatePosition();
	const deletePosition = useDeletePosition();

	const closeForm = () => {
		setFormOpen(false);
		setEditing(null);
	};

	const handleSubmit = (values: PositionFormValues) => {
		if (editing) {
			updatePosition.mutate({ id: editing.id, input: values }, { onSuccess: closeForm });
		} else {
			createPosition.mutate(values, { onSuccess: closeForm });
		}
	};

	const columns: ColumnsType<Position> = [
		{
			title: t("page.columnName"),
			dataIndex: "name",
			key: "name",
		},
		{
			title: t("page.columnActions"),
			key: "actions",
			align: "right",
			render: (_, position) => (
				<Space>
					<PermissionGate resource="positions" action="update">
						<Button
							type="text"
							icon={<EditOutlined />}
							onClick={() => {
								setEditing(position);
								setFormOpen(true);
							}}
							title={t("page.edit")}
						/>
					</PermissionGate>
					<PermissionGate resource="positions" action="delete">
						<Popconfirm
							title={t("deleteConfirm.title")}
							description={t("deleteConfirm.description")}
							okText={t("deleteConfirm.okText")}
							cancelText={t("deleteConfirm.cancelText")}
							okButtonProps={{ danger: true }}
							onConfirm={() => deletePosition.mutate(position.id)}
						>
							<Button type="text" danger icon={<DeleteOutlined />} title={t("page.delete")} />
						</Popconfirm>
					</PermissionGate>
				</Space>
			),
		},
	];

	const positionsTabContent = (
		<div>
			<div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
				<Title level={3} className="!mb-0">
					{t("page.title")}
				</Title>
				<PermissionGate resource="positions" action="create">
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => {
							setEditing(null);
							setFormOpen(true);
						}}
					>
						{t("page.createButton")}
					</Button>
				</PermissionGate>
			</div>

			{isError && (
				<Alert
					className="mb-4"
					type="error"
					showIcon
					title={t("page.loadError")}
					description={t("page.loadErrorDescription")}
				/>
			)}

			<Table<Position>
				columns={columns}
				dataSource={positions}
				rowKey="id"
				loading={isLoading}
				pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100] }}
			/>

			<PositionFormModal
				open={formOpen}
				editing={editing}
				isSubmitting={createPosition.isPending || updatePosition.isPending}
				onCancel={closeForm}
				onSubmit={handleSubmit}
			/>
		</div>
	);

	const items = [
		{
			key: "positions",
			label: t("tabs.positions"),
			children: positionsTabContent,
		},
		/**
		 * `department-grades` FAQAT admin'ga beriladi (backend
		 * `accessControl/index.ts`) — yorliq `department-grades:read`
		 * bo'lmagan foydalanuvchiga UMUMAN ko'rsatilmaydi, bo'sh sahifa
		 * o'rniga.
		 */
		...(can("department-grades", "read")
			? [
					{
						key: "grades",
						label: t("tabs.grades"),
						children: <GradeCatalogTab />,
					},
				]
			: []),
	];

	return (
		<div>
			<Tabs items={items} />
		</div>
	);
}
